import { delay, put, select, takeLatest, all, call } from 'redux-saga/effects';
import { formatUnits, Provider, Signer } from 'ethers';
import { Errors } from '@blink/components/src/constants/errors';
import Logger from '@blink/components/src/utils/logger';

import { getWalletInfoSaga } from './common';

import { GET_USERS_SUPPLIES } from '@/shared/store/supplies/constants';
import { setUsersSupplies } from '@/shared/store/supplies/actions';
import { Store } from '@/shared/store';
import {
    clearErrorsAction,
    usersSuppliesErrorAction,
    userSuppliesBalanceErrorAction,
    userSuppliesRewardsErrorAction,
} from '@/shared/store/errors/actions';
import { UserSupply } from '@/shared/store/supplies';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import ERC4626ABI from '@/shared/abi/ERC4626ABI.json';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import {
    USERS_SUPPLIES_BALANCE_ERROR,
    USERS_SUPPLIES_ERROR,
    USERS_SUPPLIES_PENDING_REWARDS_ERROR,
} from '@/shared/store/errors/constants';
import { getWalletEarnings } from '@/shared/api/earnings';

const PULL_INTERVAL = 10000;

/**
 *  Clear errors
 */
function* clearUserSuppliesErrors() {
    yield all([
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_ERROR })),
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_BALANCE_ERROR })),
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_PENDING_REWARDS_ERROR })),
    ]);
}

/**
 * Get user balance
 */
function* fetchUserSuppliesBalance(
    agreements: Agreement[],
    walletAddress: string,
    signer: Signer | Provider,
): Generator<any, UserSupply[], any> {
    const userSupplies: UserSupply[] = yield all(
        agreements
            .filter((agr) => agr.address && agr.role === ROLE.LENDER)
            .map((agr) =>
                call(function* (): Generator<any, UserSupply | null, any> {
                    try {
                        const agreement = getSafeContract(agr.address, AgreementABI, signer);
                        let balance: bigint = yield agreement.balanceOf(walletAddress);

                        if (agr.version === 2) {
                            const contract = getSafeContract(agr.address, ERC4626ABI, signer);
                            balance = yield contract.maxWithdraw(walletAddress);
                        }

                        if (balance !== 0n) {
                            return {
                                agreementAddress: agr.address,
                                agreementTitle: agr.title,
                                totalDeposited: agr.totalDeposited,
                                totalDepositThreshold: agr.totalDepositThreshold,
                                balance: formatUnits(balance, agr.leverage.decimals),
                                reward: 'N/A',
                                apy: agr.apy,
                                tokenAddress: agr.leverage.address,
                                tokenDecimals: agr.leverage.decimals,
                                tokenSymbol: agr.leverage.symbol,
                            };
                        }
                    } catch (error) {
                        Logger.error('Error in supplies:agreement:balanceOf', { error });

                        yield put(
                            userSuppliesBalanceErrorAction({
                                message: Errors.USERS_SUPPLIES_BALANCE_ERROR + ' ' + agr.title,
                            }),
                        );
                    }
                    return null;
                }),
            ),
    );

    return userSupplies.filter((supply): supply is UserSupply => supply !== null);
}

/**
 * Rewards refresh
 */
function* fetchUserPendingRewardsLoop(
    agreements: Agreement[],
    walletAddress: string,
    signer: Signer | Provider,
    userSupplies: UserSupply[],
) {
    while (userSupplies.length) {
        yield all(
            agreements
                .filter((agr) => agr.address && agr.role === ROLE.LENDER)
                .map((agr) =>
                    call(function* () {
                        if (agr.version === 2) {
                            yield* handleAgreementV2(agr, walletAddress, userSupplies);
                        } else {
                            yield* handleAgreementV1(agr, walletAddress, signer, userSupplies);
                        }
                    }),
                ),
        );

        yield put(setUsersSupplies([...userSupplies]));
        yield delay(PULL_INTERVAL);
    }
}

function* handleAgreementV1(
    agr: Agreement,
    walletAddress: string,
    signer: Signer | Provider,
    userSupplies: UserSupply[],
): Generator<any, void, any> {
    try {
        const agreement = getSafeContract(agr.address, AgreementABI, signer);
        const reward = yield agreement.pendingRewards(walletAddress);

        const interestAccrued = formatUnits(reward as any, agr.leverage.decimals);
        agr.interestAccrued = interestAccrued;

        const supply = userSupplies.find((supply) => supply.agreementAddress === agr.address);
        if (supply) {
            supply.reward = interestAccrued;
        }
    } catch (error) {
        Logger.error('Error in supplies:agreement:pendingRewards', { error });

        yield put(
            userSuppliesRewardsErrorAction({
                message: `${Errors.USERS_SUPPLIES_PENDING_REWARDS_ERROR} ${agr.title}`,
            }),
        );
    }
}

function* handleAgreementV2(
    agr: Agreement,
    walletAddress: string,
    userSupplies: UserSupply[],
): Generator<any, void, any> {
    try {
        const validSupplies = userSupplies.filter((supply) => {
            const balance = parseFloat(supply.balance || '0');
            return balance > 0 && !!supply.agreementAddress;
        });

        const pools = validSupplies.map((supply) => supply.agreementAddress!);

        const earnings = yield call(getWalletEarnings, {
            walletAddress,
            pools,
        });

        for (const currentSupply of validSupplies) {
            const earning = earnings.find(
                (e: { poolAddress: string }) =>
                    e.poolAddress.toLowerCase() === currentSupply.agreementAddress!.toLowerCase(),
            );

            if (earning) {
                const totalProfit = earning.performance.totalProfit;

                const supply = userSupplies.find(
                    (supply) => supply.agreementAddress === currentSupply.agreementAddress,
                );

                if (supply) {
                    supply.reward = totalProfit;
                }
                currentSupply.apy = earning.performance.apy;

                currentSupply.totalProfit = totalProfit;
            }
        }
    } catch (error) {
        Logger.error('Error in supplies:agreement:pendingRewards', { error });

        yield put(
            userSuppliesRewardsErrorAction({
                message: `${Errors.USERS_SUPPLIES_PENDING_REWARDS_ERROR} ${agr.title}`,
            }),
        );
    }
}

/**
 * Main saga
 */
export function* getUserSuppliesSaga(): Generator<any, any, any> {
    try {
        yield call(clearUserSuppliesErrors);

        const { agreements } = yield select((state: Store) => ({
            agreements: state.agreements.lenderAgreements,
        }));

        const walletData = yield call(getWalletInfoSaga, () => setUsersSupplies([]));
        if (!walletData) return;
        const { walletAddress, signer } = walletData;

        const userSupplies = yield call(
            fetchUserSuppliesBalance,
            agreements,
            walletAddress,
            signer,
        );
        yield put(setUsersSupplies(userSupplies));

        yield call(fetchUserPendingRewardsLoop, agreements, walletAddress, signer, userSupplies);
    } catch (e: any) {
        yield put(setUsersSupplies([]));
        yield put(
            usersSuppliesErrorAction({
                message:
                    e?.status === 403 || e?.status === 431
                        ? Errors.PERMISSION_DENIED + ' to get user supplies'
                        : Errors.USERS_SUPPLIES + ' ' + e.message,
            }),
        );
        Logger.error('Error in agreements::getUserSuppliesSaga:', { error: e });
    }
}

export function* suppliesWatcher() {
    yield takeLatest(GET_USERS_SUPPLIES, getUserSuppliesSaga);
}
